import type { Dimension } from '@pubstack/common/src/analytics/dimension'
import { SparkForPubMetricsByBidder } from '@pubstack/common/src/analytics/query'
import type { CurrencySymbol } from '@pubstack/common/src/currency'
import { type FunctionComponent, useMemo } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { MultipleDetailedDataTable } from '~/components/DataTable/DataTable'
import { Widget, type WidgetProps } from '~/components/Widget'
import { type AnalyticsDataTableConfigs, useAnalyticsDataTable } from '~/modules/analytics/analyticsDataTable.hooks'
import {
  serverSideImpressionCount,
  serverSideImpressionRevenue,
  sparkForPubAdCallCount,
  sparkForPubRevenueUplift,
  sparkForPubRevenueUpliftPct,
  sparkForPubRpmUpliftAmount,
  sparkForPubTotalRpm,
} from '~/modules/analytics/common/formulas'
import type { Context } from '~/state'
import type { WithClassName } from '~/types/utils'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'

type SparkForPubBreakdownProps = WithClassName &
  Omit<WidgetProps, 'title' | 'icon' | 'info' | 'refreshTrackingLabel' | 'onRefreshClick'> & {
    context: Context
    currencySymbol: CurrencySymbol
    dimensions: Dimension[]
  }

type SparkRawData = {
  controlImpressionCpmSum?: (number | null)[]
  controlAdCallCount?: (number | null)[]
  sparkImpressionCpmSum?: (number | null)[]
  sparkAdCallCount?: (number | null)[]
}

// Combined (control + S4P) columns; Revenue Uplift and RPM Uplift are N/A when the bidder has no control sample.
const hasControlSample = ({ rawData, index }: { rawData: SparkRawData; index: number }) => (rawData.controlAdCallCount?.[index] ?? 0) !== 0 && (rawData.controlImpressionCpmSum?.[index] ?? 0) !== 0
const hasSparkSample = ({ rawData, index }: { rawData: SparkRawData; index: number }) => hasControlSample({ rawData, index }) && (rawData.sparkAdCallCount?.[index] ?? 0) !== 0

const DATA_CONFIG: AnalyticsDataTableConfigs<string> = [
  [
    { displayedAs: 'text', sortLabel: 'Name' },
    {
      formula: sparkForPubRevenueUpliftPct,
      propertyName: 'sparkUpliftPct',
      isComputable: hasControlSample,
      cellStatus: (rawValue) => (rawValue !== undefined && rawValue > 0 ? 'success' : undefined),
    },
    { formula: serverSideImpressionRevenue, propertyName: 'impressionRevenue', tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_IMPRESSION_REVENUE },
    { formula: sparkForPubRevenueUplift, propertyName: 'revenueUplift', isComputable: hasControlSample },
    { formula: sparkForPubTotalRpm, propertyName: 'rpm' },
    { formula: sparkForPubRpmUpliftAmount, propertyName: 'rpmUplift', isComputable: hasSparkSample },
    { formula: sparkForPubAdCallCount, propertyName: 'adCallCount' },
    { formula: serverSideImpressionCount, propertyName: 'impressionCount' },
  ],
]

const COLUMNS_CONFIG = ['sparkUpliftPct', 'impressionRevenue', 'revenueUplift', 'rpm', 'rpmUplift', 'adCallCount', 'impressionCount']

export const SparkForPubBreakdown: FunctionComponent<SparkForPubBreakdownProps> = ({ context, dimensions, currencySymbol, ...props }) => {
  const analyticsQueryByBidder = useAnalyticsQueryTanstack<SparkForPubMetricsByBidder>('spark-for-pub.metrics.by.bidder', dimensions, context, {})

  const dataConfig = useMemo(() => DATA_CONFIG, [])
  const columnsConfig = useMemo(() => COLUMNS_CONFIG, [])

  const { data, columns, sorting } = useAnalyticsDataTable({
    rawData: analyticsQueryByBidder.data,
    currencySymbol,
    dataConfigs: dataConfig,
    columnsConfigs: columnsConfig,
    defaultSort: 'impressionRevenue',
  })

  return (
    <Widget
      {...props}
      isLoading={analyticsQueryByBidder.isFetching}
      error={analyticsQueryByBidder.isError}
      empty={!analyticsQueryByBidder.isFetching && !analyticsQueryByBidder?.data?.name.length}
      onRefreshClick={() => analyticsQueryByBidder.refetch()}
      icon={'bidder'}
      title={'Overview per bidder'}
      refreshTrackingLabel={'ana-spark-for-pub-refresh'}
    >
      <MultipleDetailedDataTable columns={columns} rows={data} sorting={sorting} />
    </Widget>
  )
}
